import {
	SETTINGS_VERSION,
	type SettingDefinition,
	type SettingValueType,
	type SettingValue,
	type SettingsContribution,
	type SettingsPreset,
	type StoredSettings
} from './types.js'
import { validateSettingValue, clampNumber } from './validation.js'
import {
	loadSettingsFromStorage,
	persistSettingsToStorage,
	buildExportEnvelope
} from './local-storage.js'

let definitions = $state<Map<string, SettingDefinition>>(new Map())
let contributions = $state<SettingsContribution[]>([])
let presets = $state<Map<string, SettingsPreset>>(new Map())
let userValues = $state<StoredSettings>({})
let activePresetId = $state<string | null>(null)

const stored = loadSettingsFromStorage()
userValues = stored.values
activePresetId = stored.activePreset

function getEffectiveDefault(key: string): unknown {
	if (activePresetId) {
		const preset = presets.get(activePresetId)
		if (preset && key in preset.values) {
			return preset.values[key]
		}
	}
	return definitions.get(key)?.defaultValue
}

function persist(): void {
	persistSettingsToStorage(userValues, activePresetId)
}

export function registerSettings(contribution: SettingsContribution): void {
	if (contributions.some((c) => c.namespace === contribution.namespace)) return
	contributions = [...contributions, contribution]
	const updated = new Map(definitions)
	for (const def of contribution.definitions) {
		updated.set(def.key, def)
	}
	definitions = updated
}

export function registerPreset(preset: SettingsPreset): void {
	const updated = new Map(presets)
	updated.set(preset.id, preset)
	presets = updated
}

export function get<T extends SettingValueType>(key: string): SettingValue<T> | undefined {
	if (key in userValues) {
		return userValues[key] as SettingValue<T>
	}

	if (activePresetId) {
		const preset = presets.get(activePresetId)
		if (preset && key in preset.values) {
			return preset.values[key] as SettingValue<T>
		}
	}

	const def = definitions.get(key)
	if (def) {
		return def.defaultValue as SettingValue<T>
	}

	return undefined
}

export function getDefinition(key: string): SettingDefinition | undefined {
	return definitions.get(key)
}

export function getPresets(): SettingsPreset[] {
	return Array.from(presets.values())
}

export function getActivePreset(): string | null {
	return activePresetId
}

export function getUserValues(): StoredSettings {
	return userValues
}

export function isModified(key: string): boolean {
	return key in userValues
}

export function getDefaultValue<T extends SettingValueType>(
	key: string
): SettingValue<T> | undefined {
	const def = definitions.get(key)
	return def?.defaultValue as SettingValue<T> | undefined
}

export function set(key: string, value: unknown): void {
	const def = definitions.get(key)

	if (def) {
		const error = validateSettingValue(def, value)
		if (error) {
			console.warn(`[settings] Invalid value for "${key}": ${error}`)
			return
		}

		let resolved = value
		if (def.type === 'number' && typeof value === 'number') {
			resolved = clampNumber(value, def as SettingDefinition<'number'>)
		}

		const effectiveDefault = getEffectiveDefault(key)
		if (resolved === effectiveDefault) {
			reset(key)
			return
		}

		value = resolved
	}

	userValues = { ...userValues, [key]: value }
	persist()
}

export function reset(key: string): void {
	if (!(key in userValues)) return

	const { [key]: _, ...rest } = userValues
	userValues = rest
	persist()
}

export function resetAll(): void {
	userValues = {}
	activePresetId = null
	persist()
}

export function applyPreset(presetId: string): void {
	if (!presets.has(presetId)) {
		console.warn(`[settings] Unknown preset: "${presetId}"`)
		return
	}
	activePresetId = presetId
	userValues = {}
	persist()
}

export function setActivePresetId(presetId: string | null): void {
	activePresetId = presetId
	persist()
}

export function clearPreset(): void {
	activePresetId = null
	persist()
}

export function exportJSON(): string {
	const envelope = buildExportEnvelope(userValues, activePresetId)
	return JSON.stringify(envelope, null, 2)
}

export function importJSON(jsonStr: string): { success: boolean; errors: string[] } {
	const errors: string[] = []

	let parsed: unknown
	try {
		parsed = JSON.parse(jsonStr)
	} catch {
		return { success: false, errors: ['Invalid JSON — the file may be corrupted'] }
	}

	if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
		return { success: false, errors: ['Invalid settings format — expected a JSON object'] }
	}

	const envelope = parsed as Record<string, unknown>

	if (!('version' in envelope)) {
		return { success: false, errors: ['Missing required "version" field'] }
	}

	if (envelope.version !== SETTINGS_VERSION) {
		return { success: false, errors: [`Unsupported settings version: ${envelope.version}`] }
	}

	if (
		'values' in envelope &&
		(typeof envelope.values !== 'object' ||
			envelope.values === null ||
			Array.isArray(envelope.values))
	) {
		return { success: false, errors: ['Invalid "values" field — expected an object'] }
	}

	const values = (envelope.values ?? {}) as Record<string, unknown>
	const validValues: StoredSettings = {}

	for (const [key, value] of Object.entries(values)) {
		const def = definitions.get(key)
		if (!def) {
			errors.push(`Unknown setting: "${key}" (skipped)`)
			continue
		}

		const validationError = validateSettingValue(def, value)
		if (validationError) {
			errors.push(`${key}: ${validationError} (skipped)`)
			continue
		}

		if (def.type === 'number' && typeof value === 'number') {
			validValues[key] = clampNumber(value, def as SettingDefinition<'number'>)
		} else {
			validValues[key] = value
		}
	}

	let newActivePreset: string | null = null
	if (envelope.activePreset) {
		if (typeof envelope.activePreset !== 'string') {
			errors.push(`Invalid "activePreset" field — expected a string (ignored)`)
		} else if (presets.has(envelope.activePreset)) {
			newActivePreset = envelope.activePreset
		} else {
			errors.push(`Unknown preset: "${envelope.activePreset}" (ignored)`)
		}
	}

	userValues = validValues
	activePresetId = newActivePreset
	persist()

	return { success: true, errors }
}
